// Relay → direct upgrade monitor — task.
//
// When ICE picks a TURN-relayed candidate pair on initial connect
// (often: relay won the connectivity-check race against still-
// converging direct candidates), the session sits on relay for its
// lifetime — WebRTC has no built-in relay → direct auto-upgrade. For
// p2claw's interactive / long-lived sessions this is a real bandwidth
// leak: the user's traffic round-trips through TURN forever, and we
// pay the bandwidth bill.
//
// The monitor runs on the post-connect pc. If the nominated path is
// `relay`, it polls `pc.getStats()` periodically and, when it sees a
// direct candidate-pair in `state === "succeeded"` (the proof that
// direct is reachable but lost the original race), calls
// `pc.restartIce()` to give the browser another shot at picking the
// direct path. After `maxAttempts` attempts without an upgrade it
// gives up — staying on relay is correct when direct is genuinely
// blocked.
//
// Stop conditions:
//   * external `dispose()` (session ended, wrapper boot bailed).
//   * pc transitions to `disconnected`/`failed`/`closed` — reconnect
//     machinery owns the rebuild path; the monitor on the dead pc
//     just exits and a fresh one starts on the new pc.
//   * tab visibility goes `hidden` — pause polling (tabs in the
//     background shouldn't burn CPU on a stats poll the user can't
//     see); resume on `visible`.
//   * organic upgrade (nominated transitions to non-relay on its own,
//     e.g. continual ICE gathering on a network change).
//
// Diagnostic breadcrumbs use the `p2claw:relay-upgrade:<phase>` prefix
// (matching the reconnect logs and recovery logs) so a
// real-world user pasting their console output can be grepped for the
// monitor's full timeline post-mortem.

/** Outcome shape — only `dispose` is needed externally. */
export interface RelayUpgradeMonitor {
  dispose(): void;
}

export interface RelayUpgradeMonitorOptions {
  /** Default 30s. Overridable via `?p2claw-relay-upgrade-poll-ms=N` in
   *  the wrapper URL — see `wrapper.ts`. */
  pollIntervalMs?: number;
  /** Default 3. After this many attempts without an upgrade, give up. */
  maxAttempts?: number;
  /** Defaults to `console.info`. Hooked so unit tests can capture the
   *  diagnostic trace without polluting stdout. Same shape as the
   *  reconnect logger in `session.ts` — `(message, detail?)`. */
  logger?: (message: string, detail?: string) => void;
  /** Test seam — defaults to `globalThis.setTimeout`. */
  setTimeoutFn?(cb: () => void, ms: number): unknown;
  /** Test seam — defaults to `globalThis.clearTimeout`. */
  clearTimeoutFn?(id: unknown): void;
  /** Test seam for `document.visibilityState` / `visibilitychange`.
   *  Defaults to `globalThis.document` when present; pass `null` to
   *  opt out of visibility coupling entirely. */
  document?: VisibilityDocumentLike | null;
}

/** Narrow subset of `Document` the monitor uses for visibility
 *  coupling. Accepting a subset (rather than full `Document`) keeps
 *  the test fakes trivial and pins the contract to the two surfaces
 *  we actually touch: `visibilityState` (read) and the
 *  `visibilitychange` listener pair. */
export interface VisibilityDocumentLike {
  readonly visibilityState: DocumentVisibilityState;
  addEventListener(type: "visibilitychange", listener: () => void): void;
  removeEventListener(type: "visibilitychange", listener: () => void): void;
}

export const DEFAULT_POLL_INTERVAL_MS = 30_000;
export const DEFAULT_MAX_ATTEMPTS = 3;

/**
 * Start the monitor on a freshly-connected pc. Returns a handle whose
 * `dispose()` tears it down externally — the monitor also self-disposes
 * on pc state transitions and successful upgrade. Safe to call
 * `dispose()` multiple times; subsequent calls are no-ops.
 *
 * If the initial nominated path is NOT relay, the monitor returns a
 * silent no-op disposer — no logs, no listeners, no polls. This keeps
 * the call site at `wrapper.ts` unconditional: every `connectToBox`
 * result gets passed in, and the monitor decides whether to arm.
 *
 * Note on init timing: `pc.getStats()` is async, so the initial
 * non-relay-skip decision happens one microtask after the call. The
 * `dispose()` returned synchronously will tear down the in-flight
 * init (sets `disposed = true` so the await result is dropped), so
 * a caller that disposes immediately after starting won't leak any
 * listeners or pending polls.
 */
export function startRelayUpgradeMonitor(
  pc: RTCPeerConnection,
  opts?: RelayUpgradeMonitorOptions,
): RelayUpgradeMonitor {
  const pollIntervalMs = opts?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const maxAttempts = opts?.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  const log =
    opts?.logger ??
    ((message: string, detail?: string) =>
      detail === undefined ? console.info(message) : console.info(message, detail));
  const setTimeoutFn =
    opts?.setTimeoutFn ?? ((cb: () => void, ms: number) => setTimeout(cb, ms));
  const clearTimeoutFn =
    opts?.clearTimeoutFn ??
    ((id: unknown) => clearTimeout(id as ReturnType<typeof setTimeout>));
  // `opts.document === null` opts out; `undefined` means "use the
  // ambient document". This lets headless test environments without
  // a `document` global pass through cleanly without crashing on
  // `globalThis.document` access.
  const doc: VisibilityDocumentLike | null =
    opts?.document === null
      ? null
      : opts?.document ?? (globalThis.document as VisibilityDocumentLike | undefined) ?? null;

  let disposed = false;
  let armed = false;
  let attempt = 0;
  let timerId: unknown = null;
  let paused = false;

  function clearPendingTimer(): void {
    if (timerId !== null) {
      clearTimeoutFn(timerId);
      timerId = null;
    }
  }

  function scheduleNextPoll(): void {
    if (disposed || paused) return;
    clearPendingTimer();
    timerId = setTimeoutFn(() => {
      timerId = null;
      void runPoll();
    }, pollIntervalMs);
  }

  function teardown(reason: TeardownReason): void {
    if (disposed) return;
    disposed = true;
    clearPendingTimer();
    if (armed) {
      pc.removeEventListener("connectionstatechange", onPcStateChange);
      doc?.removeEventListener("visibilitychange", onVisibilityChange);
    }
    // Only log dispose reasons when the monitor was armed — a
    // never-armed dispose (initial path was non-relay, so we returned
    // a no-op disposer) should be entirely silent. `upgraded` and
    // `gave_up` callers log their own breadcrumb before invoking
    // teardown, so we don't double-log on those paths.
    if (armed && (reason === "dc_closed" || reason === "external")) {
      log("p2claw:relay-upgrade:disposed", `reason=${reason}`);
    }
  }

  function onPcStateChange(): void {
    const s = pc.connectionState;
    if (s === "disconnected" || s === "failed" || s === "closed") {
      teardown("dc_closed");
    }
  }

  function onVisibilityChange(): void {
    if (!doc) return;
    if (doc.visibilityState === "hidden") {
      pause();
    } else {
      resume();
    }
  }

  function pause(): void {
    if (paused || disposed) return;
    paused = true;
    clearPendingTimer();
    log("p2claw:relay-upgrade:paused", "reason=hidden");
  }

  function resume(): void {
    if (!paused || disposed) return;
    paused = false;
    log("p2claw:relay-upgrade:resumed");
    scheduleNextPoll();
  }

  async function runPoll(): Promise<void> {
    if (disposed || paused) return;
    // Coordinate with the reconnect machinery — if the pc has
    // dropped to `disconnected` (or worse) between polls, don't
    // restart-ICE on a peer that's about to be torn down. The
    // `connectionstatechange` listener will fire and teardown shortly;
    // here we just no-op the poll. (For `connecting` / `new` we also
    // skip — there's nothing to upgrade until the path is established.)
    if (pc.connectionState !== "connected") {
      scheduleNextPoll();
      return;
    }

    attempt += 1;
    log("p2claw:relay-upgrade:check", `attempt=${attempt}`);

    const stats = await readStatsSafe(pc);
    if (disposed) return;
    if (!stats) {
      // getStats failed or unavailable — try again next interval
      // rather than giving up. A transient stats failure shouldn't
      // burn an attempt.
      attempt -= 1;
      scheduleNextPoll();
      return;
    }

    const nominated = findNominatedSucceededPair(stats);
    if (!nominated) {
      // No nominated/succeeded pair surfaced this poll — pc is in a
      // transitional state (mid-restart, gathering, etc.). Try again.
      attempt -= 1;
      scheduleNextPoll();
      return;
    }
    const nominatedType = candidateTypeOf(stats, nominated.localCandidateId);

    // (a) Organic or post-restart upgrade — nominated is no longer
    // relay. We're done.
    if (nominatedType && nominatedType !== "relay") {
      log("p2claw:relay-upgrade:upgraded", `type=${nominatedType}`);
      teardown("upgraded");
      return;
    }

    // (b) Still on relay. Look for a non-relay pair that ICE has
    // marked succeeded — proof that direct is reachable, just lost
    // the nomination race.
    const directExists = hasNonRelaySucceededPair(stats);
    if (!directExists) {
      log("p2claw:relay-upgrade:no_better_path", `attempt=${attempt}`);
      if (attempt >= maxAttempts) {
        log("p2claw:relay-upgrade:gave_up", `attempts=${attempt}`);
        teardown("gave_up");
        return;
      }
      scheduleNextPoll();
      return;
    }

    // (c) Direct path exists but nominated stayed relay. If we're on
    // the last allowed attempt, restarting ICE just to give up before
    // observing the result is wasted churn — log gave_up immediately.
    // Otherwise, trigger a restart and re-check next poll.
    if (attempt >= maxAttempts) {
      log("p2claw:relay-upgrade:gave_up", `attempts=${attempt}`);
      teardown("gave_up");
      return;
    }
    let restartErr: string | null = null;
    try {
      pc.restartIce();
    } catch (e) {
      // restartIce is in-spec everywhere we ship to (Chrome 80+,
      // Safari 15+, Firefox 70+), but a fake pc in tests / a future
      // platform regression shouldn't crash the monitor. Capture the
      // throw into the breadcrumb detail so the trigger trace stays
      // 1:1 with attempts, and let the next poll observe whether the
      // path improved on its own.
      restartErr = (e as Error).message ?? String(e);
    }
    log(
      "p2claw:relay-upgrade:trigger",
      restartErr === null
        ? `attempt=${attempt}`
        : `attempt=${attempt} restartIce_threw=${restartErr}`,
    );
    scheduleNextPoll();
  }

  // ---------- initialization ----------

  void initialize();

  async function initialize(): Promise<void> {
    const stats = await readStatsSafe(pc);
    if (disposed) return;
    if (!stats) return;
    const nominated = findNominatedSucceededPair(stats);
    if (!nominated) return;
    const type = candidateTypeOf(stats, nominated.localCandidateId);
    if (type !== "relay") {
      // Not on relay — nothing to upgrade. Silent no-op, matches
      // production behavior for any direct-path session.
      return;
    }
    armed = true;
    log("p2claw:relay-upgrade:start");
    pc.addEventListener("connectionstatechange", onPcStateChange);
    doc?.addEventListener("visibilitychange", onVisibilityChange);
    if (doc?.visibilityState === "hidden") {
      // The page started hidden (background tab, or visibility
      // changed during the boot window). Park immediately and emit
      // the breadcrumb so the trace is honest about why no `check`
      // is firing.
      paused = true;
      log("p2claw:relay-upgrade:paused", "reason=hidden");
    } else {
      scheduleNextPoll();
    }
  }

  return {
    dispose(): void {
      teardown("external");
    },
  };
}

// ---------- internal helpers ----------

type TeardownReason = "external" | "dc_closed" | "upgraded" | "gave_up";

interface CandidatePair {
  readonly type: "candidate-pair";
  readonly nominated: boolean;
  readonly state: string;
  readonly localCandidateId: string;
}

interface CandidateReport {
  readonly type?: string;
  readonly candidateType?: string;
}

/**
 * Read `pc.getStats()` and adapt the report to the iterable/Map shape
 * we use throughout. Returns null on failure (no `getStats`, throws,
 * etc.) so the caller can treat the poll as a no-op without burning an
 * attempt.
 */
async function readStatsSafe(
  pc: RTCPeerConnection,
): Promise<Map<string, unknown> | null> {
  try {
    if (typeof pc.getStats !== "function") return null;
    const stats = await pc.getStats();
    return stats as unknown as Map<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Locate the single nominated, succeeded candidate-pair report.
 * Returns its destructured shape (with `localCandidateId`) so callers
 * don't have to re-cast. ICE may briefly nominate multiple pairs
 * during a restart; we take the first match — by spec there's exactly
 * one nominated pair at a time in steady state.
 */
function findNominatedSucceededPair(
  stats: Map<string, unknown>,
): CandidatePair | null {
  for (const report of stats.values()) {
    const r = report as Partial<CandidatePair>;
    if (
      r.type === "candidate-pair" &&
      r.nominated === true &&
      r.state === "succeeded" &&
      typeof r.localCandidateId === "string"
    ) {
      return r as CandidatePair;
    }
  }
  return null;
}

/** Return the `candidateType` of a local candidate by id, or null. */
function candidateTypeOf(
  stats: Map<string, unknown>,
  localCandidateId: string,
): string | null {
  const local = stats.get(localCandidateId) as CandidateReport | undefined;
  return typeof local?.candidateType === "string" ? local.candidateType : null;
}

/**
 * Does `stats` contain ANY succeeded candidate-pair whose local
 * candidate is non-relay? This is the test for "direct path is
 * reachable, just lost the nomination race". Pairs in
 * `state === "in-progress"` don't count — we want hard evidence the
 * connectivity check completed.
 */
function hasNonRelaySucceededPair(stats: Map<string, unknown>): boolean {
  for (const report of stats.values()) {
    const r = report as Partial<CandidatePair>;
    if (
      r.type === "candidate-pair" &&
      r.state === "succeeded" &&
      typeof r.localCandidateId === "string"
    ) {
      const t = candidateTypeOf(stats, r.localCandidateId);
      if (t && t !== "relay") return true;
    }
  }
  return false;
}
